import type { Session } from '@supabase/supabase-js';
import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { PageBoundary } from './components.tsx';
import { AppContext, translator, type Role } from './context.tsx';
import { appTitle } from './lib/brand.ts';
import type { Language } from './lib/i18n.ts';
import { Chat } from './pages/Chat.tsx';
import { Login } from './pages/Login.tsx';
import { More } from './pages/More.tsx';
import { supabase } from './supabase.ts';

// The assistant opens first; the other pages load when the owner opens them.
const Overview = lazy(() => import('./pages/Overview.tsx').then((m) => ({ default: m.Overview })));
const Proposals = lazy(() =>
  import('./pages/Proposals.tsx').then((m) => ({ default: m.Proposals })),
);
const Memory = lazy(() => import('./pages/Memory.tsx').then((m) => ({ default: m.Memory })));
const Branches = lazy(() => import('./pages/Branches.tsx').then((m) => ({ default: m.Branches })));
const Settings = lazy(() => import('./pages/Settings.tsx').then((m) => ({ default: m.Settings })));
const Health = lazy(() => import('./pages/Health.tsx').then((m) => ({ default: m.Health })));

const PAGES = [
  'chat',
  'overview',
  'proposals',
  'memory',
  'branches',
  'settings',
  'health',
  'more',
] as const;
type Page = (typeof PAGES)[number];

const NAV_KEYS = {
  chat: 'navChat',
  overview: 'navOverview',
  proposals: 'navProposals',
  memory: 'navMemory',
  branches: 'navBranches',
  settings: 'navSettings',
  health: 'navHealth',
  more: 'navMore',
} as const;

/** Phones show five tabs (the rest sit under "More"); wide screens list every page but "More". */
const PHONE_TABS: readonly Page[] = ['chat', 'overview', 'proposals', 'memory', 'more'];
const navClass = (p: Page): string =>
  p === 'more' ? 'nav-item phone-only' : PHONE_TABS.includes(p) ? 'nav-item' : 'nav-item wide-only';

const LANGUAGE_KEY = 'asparagus-ads.language';

function pageFromHash(): Page {
  const hash = window.location.hash.replace(/^#\/?/, '');
  return (PAGES as readonly string[]).includes(hash) ? (hash as Page) : 'chat';
}

function storedLanguage(): Language {
  try {
    return window.localStorage.getItem(LANGUAGE_KEY) === 'en' ? 'en' : 'ar';
  } catch {
    return 'ar';
  }
}

type Access = { status: 'checking' } | { status: 'none' } | { status: 'ok'; role: Role };

export function App() {
  const [language, setLanguage] = useState<Language>(storedLanguage);
  const [session, setSession] = useState<Session | null | undefined>(undefined);
  const [access, setAccess] = useState<Access>({ status: 'checking' });
  const [page, setPage] = useState<Page>(pageFromHash);
  const t = useMemo(() => translator(language), [language]);

  useEffect(() => {
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.title = appTitle(language);
    try {
      window.localStorage.setItem(LANGUAGE_KEY, language);
    } catch {
      // Storage can be unavailable (private mode); the choice then lasts for this visit only.
    }
  }, [language]);

  useEffect(() => {
    const onHash = () => {
      setPage(pageFromHash());
    };
    window.addEventListener('hashchange', onHash);
    return () => {
      window.removeEventListener('hashchange', onHash);
    };
  }, []);

  useEffect(() => {
    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
    });
    const { data } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
    });
    return () => {
      data.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!session) return;
    let current = true;
    void supabase
      .from('app_users')
      .select('role, active')
      .eq('id', session.user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!current) return;
        const role = data?.role;
        setAccess(
          data?.active && (role === 'owner' || role === 'viewer')
            ? { status: 'ok', role }
            : { status: 'none' },
        );
      });
    return () => {
      current = false;
    };
  }, [session]);

  const toggleLanguage = () => {
    setLanguage((l) => (l === 'ar' ? 'en' : 'ar'));
  };

  if (session === undefined) return <p className="center-note">{t('loading')}</p>;
  if (session === null)
    return <Login language={language} t={t} onToggleLanguage={toggleLanguage} />;
  if (access.status === 'checking') return <p className="center-note">{t('loading')}</p>;
  if (access.status === 'none') {
    return (
      <div className="center-note">
        <p>{t('noAccess')}</p>
        <button type="button" className="button quiet" onClick={() => void supabase.auth.signOut()}>
          {t('signOut')}
        </button>
      </div>
    );
  }

  return (
    <AppContext.Provider value={{ language, role: access.role, t }}>
      <div className="shell">
        <header className="topbar">
          <span className="brand">
            <img className="brand-logo" src="/icons/icon-192.png" alt="" width={28} height={28} />
            {appTitle(language)}
          </span>
          <span className="topbar-actions">
            <button type="button" className="link-button" onClick={toggleLanguage}>
              {t('switchLanguage')}
            </button>
            <button
              type="button"
              className="link-button"
              onClick={() => void supabase.auth.signOut()}
            >
              {t('signOut')}
            </button>
          </span>
        </header>
        <nav className="nav" aria-label={appTitle(language)}>
          {PAGES.map((p) => (
            <a
              key={p}
              href={`#/${p}`}
              className={navClass(p)}
              aria-current={
                p === page || (p === 'more' && !PHONE_TABS.includes(page)) ? 'page' : undefined
              }
            >
              {t(NAV_KEYS[p])}
            </a>
          ))}
        </nav>
        <main className="content">
          <PageBoundary key={page} message={t('loadFailed')} retry={t('retry')}>
            <Suspense fallback={<p className="muted">{t('loading')}</p>}>
              {page === 'chat' && <Chat />}
              {page === 'overview' && <Overview />}
              {page === 'memory' && <Memory />}
              {page === 'more' && <More onToggleLanguage={toggleLanguage} />}
              {page === 'proposals' && <Proposals />}
              {page === 'branches' && <Branches />}
              {page === 'settings' && <Settings />}
              {page === 'health' && <Health />}
            </Suspense>
          </PageBoundary>
        </main>
      </div>
    </AppContext.Provider>
  );
}
